import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2FormField,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { apiDelete, apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import {
  NO_PROJECT_LABEL,
  TASK_STATUS_OPTIONS,
  type ProjectTask,
} from '../../lib/types/projects.js';

type TaskStatus = (typeof TASK_STATUS_OPTIONS)[number]['value'];

const STATUS_BY_LABEL = new Map<string, TaskStatus>(
  TASK_STATUS_OPTIONS.map((opt) => [opt.label, opt.value]),
);

const STATUS_TONE: Record<TaskStatus, V2BadgeTone> = {
  todo: 'neutral',
  in_progress: 'info',
  done: 'success',
  cancelled: 'danger',
};

interface StatusAction {
  readonly to: TaskStatus;
  readonly label: string;
  readonly variant: 'primary' | 'secondary' | 'danger';
  readonly confirm?: string;
}

/**
 * The actions offered per status mirror the server's TASK_STATUS_TRANSITIONS
 * (packages/modules/projects/src/transitions.ts): todo → in_progress |
 * cancelled; in_progress → done | todo | cancelled; done/cancelled are final.
 * This only decides which buttons to show — the PATCH is authoritative and
 * rejects anything else (422), and record scope decides who may change it.
 */
const STATUS_ACTIONS: Record<TaskStatus, readonly StatusAction[]> = {
  todo: [
    { to: 'in_progress', label: 'شروع کار', variant: 'primary' },
    {
      to: 'cancelled',
      label: 'لغو کار',
      variant: 'danger',
      confirm: 'کار لغو شود؟ کار لغوشده دوباره باز نمی‌شود.',
    },
  ],
  in_progress: [
    { to: 'done', label: 'انجام شد', variant: 'primary' },
    { to: 'todo', label: 'بازگشت به انجام‌نشده', variant: 'secondary' },
    {
      to: 'cancelled',
      label: 'لغو کار',
      variant: 'danger',
      confirm: 'کار لغو شود؟ کار لغوشده دوباره باز نمی‌شود.',
    },
  ],
  done: [],
  cancelled: [],
};

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 20px',
        borderBottom: '1px solid var(--sk-divider)',
        minHeight: 44,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ flex: '0 0 96px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>{label}</span>
      <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: 14, overflowWrap: 'anywhere' }}>
        {children}
      </span>
    </div>
  );
}

/**
 * Task detail with status actions — the «مشاهده» target of every task row in
 * the topbar «کارهای من» popover, the mobile bottom-nav «کارها» page and the
 * Projects › کارها list. Real endpoints: GET/PATCH t/:slug/api/v1/projects/
 * tasks/:ref (apps/api/src/projects/{hub,commands}.controller.ts).
 * state reference: ready · not_found (404, also a record outside the caller's
 * scope) · permission_denied · network_error · loading
 */
export function TaskDetailPage() {
  const { ref } = useParams<{ ref: string }>();
  // Keyed by task ref: React Router keeps this route mounted when the topbar
  // «مشاهده» jumps from task A to task B, so without a remount a pending
  // cancel confirmation (or error/notice) opened for A would survive and a
  // confirm click would PATCH B. A new key discards all per-task state.
  return <TaskDetail key={ref} />;
}

function TaskDetail() {
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [pending, setPending] = useState<TaskStatus | null>(null);
  const [confirming, setConfirming] = useState<StatusAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newStepTitle, setNewStepTitle] = useState('');
  const [addingStep, setAddingStep] = useState(false);
  const [spentHours, setSpentHours] = useState('');
  const [spentMins, setSpentMins] = useState('');
  const [spentDate, setSpentDate] = useState('');
  const [timeDescription, setTimeDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [loggingTime, setLoggingTime] = useState(false);

  const task = useApiGet<ProjectTask>(
    slug && ref ? `${base}/api/v1/projects/tasks/${encodeURIComponent(ref)}` : null,
    [refreshKey],
  );

  if (!slug || !ref) return null;

  async function toggleChecklist(itemId: string, currentDone: boolean) {
    try {
      await apiPatch(
        `${base}/api/v1/projects/tasks/${encodeURIComponent(ref!)}/checklist/${itemId}`,
        {
          isDone: !currentDone,
        },
      );
      setRefreshKey((k) => k + 1);
    } catch {
      setError('تغییر وضعیت مرحله چک‌لیست ناموفق بود.');
    }
  }

  async function addChecklistStep(e: React.FormEvent) {
    e.preventDefault();
    if (!newStepTitle.trim()) return;
    setAddingStep(true);
    try {
      await apiPost(`${base}/api/v1/projects/tasks/${encodeURIComponent(ref!)}/checklist`, {
        title: newStepTitle.trim(),
      });
      setNewStepTitle('');
      setRefreshKey((k) => k + 1);
    } catch {
      setError('افزودن مرحله چک‌لیست ناموفق بود.');
    } finally {
      setAddingStep(false);
    }
  }

  async function deleteChecklistStep(itemId: string) {
    try {
      await apiDelete(
        `${base}/api/v1/projects/tasks/${encodeURIComponent(ref!)}/checklist/${itemId}`,
      );
      setRefreshKey((k) => k + 1);
    } catch {
      setError('حذف مرحله چک‌لیست ناموفق بود.');
    }
  }

  async function logTime(e: React.FormEvent) {
    e.preventDefault();
    const h = parseInt(spentHours, 10) || 0;
    const m = parseInt(spentMins, 10) || 0;
    const totalMinutes = h * 60 + m;
    if (totalMinutes <= 0) {
      setError('مدت زمان کارکرد را مشخص کنید (حداقل ۱ دقیقه).');
      return;
    }
    setLoggingTime(true);
    try {
      await apiPost(`${base}/api/v1/projects/tasks/${encodeURIComponent(ref!)}/time-entries`, {
        durationMinutes: totalMinutes,
        spentDate: spentDate || undefined,
        description: timeDescription.trim() || undefined,
        billable: isBillable,
      });
      setSpentHours('');
      setSpentMins('');
      setTimeDescription('');
      setNotice('کارکرد زمانی ثبت شد.');
      setRefreshKey((k) => k + 1);
    } catch {
      setError('ثبت کارکرد زمانی ناموفق بود.');
    } finally {
      setLoggingTime(false);
    }
  }

  async function deleteLoggedTime(entryId: string) {
    try {
      await apiDelete(
        `${base}/api/v1/projects/tasks/${encodeURIComponent(ref!)}/time-entries/${entryId}`,
      );
      setNotice('کارکرد زمانی حذف شد.');
      setRefreshKey((k) => k + 1);
    } catch {
      setError('حذف کارکرد ناموفق بود.');
    }
  }

  async function changeStatus(action: StatusAction) {
    setConfirming(null);
    setError(null);
    setNotice(null);
    setPending(action.to);
    try {
      await apiPatch(`${base}/api/v1/projects/tasks/${encodeURIComponent(ref!)}`, {
        status: action.to,
      });
      setNotice(`وضعیت کار به «${labelOf(action.to)}» تغییر کرد.`);
      setRefreshKey((k) => k + 1);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) {
        setError('اجازهٔ تغییر وضعیت این کار را ندارید.');
      } else if (caught instanceof ApiError && caught.status === 422) {
        setError('این تغییر وضعیت مجاز نیست؛ صفحه را دوباره بارگذاری کنید.');
      } else {
        setError('تغییر وضعیت ناموفق بود.');
      }
    } finally {
      setPending(null);
    }
  }

  const backLink = (
    <Link
      to={`${base}/projects/tasks`}
      style={{
        fontSize: 13,
        color: 'var(--prj-link, var(--sk-brand-600))',
        textDecoration: 'none',
      }}
    >
      → بازگشت به کارها
    </Link>
  );

  if (task.status === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {backLink}
        <V2Card>
          <div
            aria-busy="true"
            style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 20 }}
          >
            <div className="sk-skel" style={{ height: 18, width: '60%' }} />
            <div className="sk-skel" style={{ height: 12 }} />
            <div className="sk-skel" style={{ height: 12 }} />
            <div className="sk-skel" style={{ height: 12, width: '40%' }} />
          </div>
        </V2Card>
      </div>
    );
  }

  if (task.status === 'error') {
    const status = task.error.status;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {backLink}
        <StatePanel
          variant={
            status === 403 ? 'permission_denied' : status === 404 ? 'empty' : 'network_error'
          }
          title={
            status === 403
              ? 'دسترسی ندارید'
              : status === 404
                ? 'این کار پیدا نشد یا در دسترس شما نیست.'
                : 'اطلاعات کار قابل نمایش نیست'
          }
        />
      </div>
    );
  }

  const t = task.data;
  const status = STATUS_BY_LABEL.get(t.statusLabel);
  const actions = status ? STATUS_ACTIONS[status] : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {backLink}

      <V2Card>
        <V2CardHeader
          title={t.title}
          subtitle={
            <span className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
              {t.ref}
            </span>
          }
          actions={
            <V2Badge tone={status ? STATUS_TONE[status] : 'neutral'}>{t.statusLabel}</V2Badge>
          }
        />
        <DetailRow label="پروژه">
          {t.projectRef ? (
            <Link
              to={`${base}/projects/list/${encodeURIComponent(t.projectRef)}`}
              style={{
                color: 'var(--prj-link, var(--sk-brand-600))',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              {t.projectRef}
            </Link>
          ) : (
            NO_PROJECT_LABEL
          )}
        </DetailRow>
        <DetailRow label="مسئول">{t.assignee?.displayLabel ?? 'بدون مسئول'}</DetailRow>
        <DetailRow label="ددلاین">{displayJalaliDate(t.dueDate)}</DetailRow>
        <DetailRow label="اولویت">{t.priorityLabel}</DetailRow>
        {t.relatedType ? (
          <DetailRow label="مرتبط با">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <V2Badge tone="info">{t.relatedTypeLabel ?? t.relatedType}</V2Badge>
              {t.relatedRef ? (
                <span style={{ fontWeight: 600, color: 'var(--sk-fg-emphasis)' }}>
                  {t.relatedRef}
                </span>
              ) : null}
            </div>
          </DetailRow>
        ) : null}
      </V2Card>

      <V2Card>
        <V2CardHeader
          title="چک‌لیست و مراحل اجرایی (Sub-tasks)"
          subtitle={
            (t.checklist ?? []).length > 0
              ? `${(t.checklist ?? []).filter((i) => i.isDone).length} از ${(t.checklist ?? []).length} مرحله انجام شد (${Math.round(((t.checklist ?? []).filter((i) => i.isDone).length / (t.checklist ?? []).length) * 100)}٪)`
              : 'مراحل گام‌به‌گام کار را تعریف کنید'
          }
          actions={
            (t.checklist ?? []).length > 0 ? (
              <V2Badge tone={(t.checklist ?? []).every((i) => i.isDone) ? 'success' : 'info'}>
                {(t.checklist ?? []).filter((i) => i.isDone).length} / {(t.checklist ?? []).length}
              </V2Badge>
            ) : null
          }
        />
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 20px 20px' }}
        >
          {(t.checklist ?? []).length > 0 ? (
            <div
              style={{
                width: '100%',
                background: 'var(--sk-bg-sunken)',
                height: 8,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.round(((t.checklist ?? []).filter((i) => i.isDone).length / (t.checklist ?? []).length) * 100)}%`,
                  height: '100%',
                  background: (t.checklist ?? []).every((i) => i.isDone)
                    ? 'var(--sk-success)'
                    : 'var(--sk-brand-500)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          ) : null}

          {(t.checklist ?? []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)', padding: '6px 0' }}>
              هنوز مرحله‌ای برای این کار ثبت نشده است. با افزودن مراحل، روند پیشرفت پروژه دقیق‌تر
              پیگیری می‌شود.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(t.checklist ?? []).map((item) => (
                <div
                  key={item.itemId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: item.isDone ? 'var(--sk-bg-sunken)' : 'var(--sk-bg-elev)',
                    border: '1px solid var(--sk-divider)',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      flex: 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.isDone}
                      onChange={() => void toggleChecklist(item.itemId, item.isDone)}
                      style={{
                        width: 18,
                        height: 18,
                        cursor: 'pointer',
                        accentColor: 'var(--sk-brand-500)',
                      }}
                    />
                    <span
                      style={{
                        fontSize: 14,
                        textDecoration: item.isDone ? 'line-through' : 'none',
                        color: item.isDone ? 'var(--sk-fg-muted)' : 'var(--sk-fg)',
                      }}
                    >
                      {item.title}
                    </span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {item.completedAt ? (
                      <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                        {displayJalaliDate(item.completedAt.slice(0, 10))}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void deleteChecklistStep(item.itemId)}
                      title="حذف مرحله"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--prj-danger-text, var(--sk-danger))',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: 15,
                        opacity: 0.7,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={addChecklistStep} style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <input
              className="sk-input"
              value={newStepTitle}
              onChange={(e) => setNewStepTitle(e.target.value)}
              placeholder="افزودن مرحله جدید (مثلاً تماس با مشتری یا آماده‌سازی پیش‌نویس)..."
              style={{ flex: 1 }}
            />
            <V2Button
              type="submit"
              variant="secondary"
              disabled={addingStep || !newStepTitle.trim()}
            >
              {addingStep ? '…' : '+ افزودن گام'}
            </V2Button>
          </form>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader
          title="ثبت و گزارش ساعت کارکرد (تایم‌شیت)"
          subtitle="ثبت نفر/ساعت کارکرد روی این وظیفه جهت کنترل هزینه‌ها و بهره‌وری"
          actions={
            <V2Badge tone="info">
              مجموع: {Math.floor((t.totalSpentMinutes ?? 0) / 60)} ساعت و{' '}
              {(t.totalSpentMinutes ?? 0) % 60} دقیقه
            </V2Badge>
          }
        />
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 20px 20px' }}
        >
          <form
            onSubmit={logTime}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 12,
              padding: 14,
              borderRadius: 8,
              background: 'var(--sk-bg-sunken)',
              border: '1px solid var(--sk-divider)',
              alignItems: 'end',
            }}
          >
            <V2FormField label="تاریخ کارکرد" hint="پیش‌فرض: امروز">
              <JalaliDateInput value={spentDate} onChange={setSpentDate} />
            </V2FormField>

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <V2FormField label="ساعت">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="sk-input"
                    value={spentHours}
                    onChange={(e) => setSpentHours(e.target.value)}
                    placeholder="ساعت"
                  />
                </V2FormField>
              </div>
              <div style={{ flex: 1 }}>
                <V2FormField label="دقیقه">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    className="sk-input"
                    value={spentMins}
                    onChange={(e) => setSpentMins(e.target.value)}
                    placeholder="دقیقه"
                  />
                </V2FormField>
              </div>
            </div>

            <V2FormField label="شرح فعالیت (اختیاری)">
              <input
                className="sk-input"
                value={timeDescription}
                onChange={(e) => setTimeDescription(e.target.value)}
                placeholder="توضیح مختصر کار..."
              />
            </V2FormField>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isBillable}
                  onChange={(e) => setIsBillable(e.target.checked)}
                  style={{ accentColor: 'var(--sk-brand-500)' }}
                />
                قابل فاکتور
              </label>

              <V2Button type="submit" variant="primary" disabled={loggingTime} style={{ flex: 1 }}>
                {loggingTime ? 'در حال ثبت…' : 'ثبت کارکرد'}
              </V2Button>
            </div>
          </form>

          {(t.timeEntries ?? []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)', padding: '6px 0' }}>
              هنوز کارکردی برای این کار ثبت نشده است.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr
                    style={{
                      borderBottom: '2px solid var(--sk-divider)',
                      textAlign: 'right',
                      color: 'var(--sk-fg-muted)',
                    }}
                  >
                    <th style={{ padding: '8px 10px' }}>تاریخ</th>
                    <th style={{ padding: '8px 10px' }}>مدت زمان</th>
                    <th style={{ padding: '8px 10px' }}>شرح فعالیت</th>
                    <th style={{ padding: '8px 10px' }}>نوع</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center' }}>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {(t.timeEntries ?? []).map((entry) => {
                    const hours = Math.floor(entry.durationMinutes / 60);
                    const mins = entry.durationMinutes % 60;
                    return (
                      <tr
                        key={entry.entryId}
                        style={{ borderBottom: '1px solid var(--sk-divider)' }}
                      >
                        <td style={{ padding: '10px' }}>{displayJalaliDate(entry.spentDate)}</td>
                        <td style={{ padding: '10px', fontWeight: 600 }}>
                          {hours > 0 ? `${hours} ساعت ` : ''}
                          {mins > 0 ? `${mins} دقیقه` : ''}
                        </td>
                        <td style={{ padding: '10px', color: 'var(--sk-fg-muted)' }}>
                          {entry.description ?? '—'}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <V2Badge tone={entry.billable ? 'success' : 'neutral'}>
                            {entry.billable ? 'قابل محاسبه' : 'داخلی'}
                          </V2Badge>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => void deleteLoggedTime(entry.entryId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--prj-danger-text, var(--sk-danger))',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              fontSize: 13,
                            }}
                          >
                            حذف
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="وضعیت کار" />
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 20px 20px' }}
        >
          {actions.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              این کار بسته شده است و وضعیت آن دیگر تغییر نمی‌کند.
            </div>
          ) : (
            <div className="prj-status-actions">
              {actions.map((action) => (
                <V2Button
                  key={action.to}
                  variant={action.variant}
                  disabled={pending !== null}
                  onClick={() =>
                    action.confirm ? setConfirming(action) : void changeStatus(action)
                  }
                >
                  {pending === action.to ? 'در حال ثبت…' : action.label}
                </V2Button>
              ))}
            </div>
          )}

          {confirming ? (
            <Alert tone="warning" title="تأیید">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span>{confirming.confirm}</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <V2Button
                    variant="danger"
                    size="sm"
                    onClick={() => void changeStatus(confirming)}
                  >
                    بله، {confirming.label}
                  </V2Button>
                  <V2Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                    انصراف
                  </V2Button>
                </div>
              </div>
            </Alert>
          ) : null}

          {notice ? (
            <Alert tone="success" title="ثبت شد">
              {notice}
            </Alert>
          ) : null}
          {error ? (
            <Alert tone="danger" title="خطا">
              {error}
            </Alert>
          ) : null}
        </div>
      </V2Card>
    </div>
  );
}

function labelOf(status: TaskStatus): string {
  return TASK_STATUS_OPTIONS.find((opt) => opt.value === status)?.label ?? status;
}
